import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';
import { PatientImportEdit } from 'src/app/patient/model/patient-import';
@Injectable({
  providedIn: 'root'
})
export class PatientImportService implements OnDestroy
{
  public patientImportStages = [
    {
      key: 'PATIENT-IMPORT-STATE-IN-REVIEW', value: 'IN_REVIEW', label: 'In Review', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-VALIDATING', value: 'VALIDATING', label: 'Performing Validations', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-VALIDATED', value: 'VALIDATED', label: 'Completed Validations', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-READY-TO-IMPORT', value: 'READY_TO_IMPORT', label: 'Ready to Import', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-IMPORTING', value: 'IMPORTING', label: 'Importing', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-READY-TO-SEND-INVITE', value: 'READY_TO_SEND_INVITE', label: 'Ready to Send Invitations', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-SENDING-INVITE', value: 'SENDING_INVITE', label: 'Sending Invitations', icon: 'envelop',
      disabled: true, isValid: true
    },
    {
      key: 'PATIENT-IMPORT-STATE-COMPLETED', value: 'COMPLETED', label: 'Completed', icon: 'check',
      disabled: true, isValid: true
    },
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get a Patient Import Batch by id.
   *
   * @param id Patient Import Batch id
   */
  public getPatientImportBatchById(id: number): Observable<PatientImportEdit>
  {
    return this.http.get<PatientImportEdit>(`/subjects/import/batch/${id}`);
  }

  /**
   * Delete a Patient Import Batch by id.
   *
   * @param id Patient Import Batch id
   */
  public deletePatientImportBatch(id: number): Observable<PatientImportEdit>
  {
    return this.http.delete<PatientImportEdit>(`/subjects/import/batch/${id}`);
  }

  /**
   * Process a Patient Import Batch by id.
   *
   * @param id Patient Import Batch id
   */
  public processPatientImportBatch(id: number): Observable<PatientImportEdit>
  {
    return this.http.post<PatientImportEdit>(`/subjects/import/process/batch/${id}`, null);
  }

  /**
   * Check if Patient Import batch name exists to avoid duplicates
   *
   * @param name Patient Import batch name
   */
  public existsPatientImportBatchName(name: string): Observable<any>
  {
    return this.http.get(`/subjects/import/batch/exists?name=${name}`);
  }

  /**
   * Save a Patient Import Batch.
   *
   * @param data Patient import batch to persit
   * @param isNew Indicate if this is a new or update
   */
  public savePatientImportBatch(data: any, isNew: boolean, id?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/subjects/import/batch', data);
    }
    else
    {
      return this.http.put(`/subjects/import/batch/${id}`, data);
    }
  }

  /**
   * Toggle patient import batch items.
   */
  public updatePatientImportBatchItems(data: any[]): Observable<any>
  {
    return this.http.put('/subjects/import/batch/items/update/discard', data);
  }

  /**
   * Update patient import batch items.
   */
  public updatePatientImportBatchItem(data: any): Observable<any>
  {
    return this.http.put('/subjects/import/batch/item/update', data);
  }

  /**
   * Get the patient import stAGE description to use
   *
   * @param importStage Import stage
   * @returns string Import stage description
   */
  public getPatientImportStageText(importStage: string): string
  {
    let text = importStage;
    const aValue = this.patientImportStages.find(x => x.value === importStage);
    if (aValue !== undefined)
    {
      text = aValue.label;
    }
    return text;
  }

  /**
   * Export the subject items to CSV
   *
   * @param id Export batch id
   * @param exportMode Export Mode: all or invalid
   * @returns Export CSV file
   */
  public getPatientExportBatchItemsToCSV(id: number, exportMode: string): Observable<any[]>
  {
    const exportQuery = exportMode !== undefined ? `?exportMode=${exportMode}` : '';
    return this.http.get<any[]>(`/subjects/export/batch/${id}${exportQuery}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    const stagesTranslationKeys = this.patientImportStages.map(a => a.key);
    this.translate.get(stagesTranslationKeys).subscribe({
      next: (res: string) =>
      {
        for (let i = 0; i < this.patientImportStages.length; i++)
        {
          const key = 'PATIENT-IMPORT-STATE-' + this.patientImportStages[i].value.replace(/_/g, '-');
          this.patientImportStages[i].label = res[key];
        }
      }
    });
  }
}
